/**
 * Outreach routing utilities for mapping between URL routes and tab indices
 * Supports conversion between legacy tab-based navigation and new URL-based routing
 */

import { OutreachCategory } from "../types/outreach";

// Route path mapping for each outreach section
export const OUTREACH_ROUTES = {
  YOUTUBE_REACTION: "youtube-reaction",
  YOUTUBE_PLAYLISTING: "youtube-playlisting",
  SPOTIFY: "spotify",
  BLOGS: "blogs",
  CAMPAIGNS: "campaigns",
  ANALYTICS: "analytics",
} as const;

// Default route - campaigns as landing page when /outreach is accessed without a view
export const DEFAULT_OUTREACH_ROUTE = OUTREACH_ROUTES.CAMPAIGNS;

/**
 * Map route name to tab index for backward compatibility
 * @param routeName - Route name from URL (e.g., "youtube-reaction")
 * @returns Tab index (0-5) or -1 if route not found
 */
export function getTabIndexFromRoute(routeName: string | undefined): number {
  if (!routeName) return -1;

  const routeMap: Record<string, number> = {
    [OUTREACH_ROUTES.YOUTUBE_REACTION]: 0,
    [OUTREACH_ROUTES.YOUTUBE_PLAYLISTING]: 1,
    [OUTREACH_ROUTES.SPOTIFY]: 2,
    [OUTREACH_ROUTES.BLOGS]: 3,
    [OUTREACH_ROUTES.CAMPAIGNS]: 4,
    [OUTREACH_ROUTES.ANALYTICS]: 5,
  };

  return routeMap[routeName] ?? -1;
}

/**
 * Map tab index to route name for URL generation
 * @param tabIndex - Tab index (0-5)
 * @returns Route name suitable for URL, or empty string if index invalid
 */
export function getRouteFromTabIndex(tabIndex: number): string {
  const indexMap: Record<number, string> = {
    0: OUTREACH_ROUTES.YOUTUBE_REACTION,
    1: OUTREACH_ROUTES.YOUTUBE_PLAYLISTING,
    2: OUTREACH_ROUTES.SPOTIFY,
    3: OUTREACH_ROUTES.BLOGS,
    4: OUTREACH_ROUTES.CAMPAIGNS,
    5: OUTREACH_ROUTES.ANALYTICS,
  };

  return indexMap[tabIndex] ?? "";
}

/**
 * Map route name to OutreachCategory
 * @param routeName - Route name from URL
 * @returns OutreachCategory or null if no mapping exists
 */
export function getCategoryFromRoute(
  routeName: string | undefined
): OutreachCategory | null {
  if (!routeName) return null;

  const categoryMap: Record<string, OutreachCategory> = {
    [OUTREACH_ROUTES.YOUTUBE_REACTION]: OutreachCategory.REACTION_CHANNELS,
    [OUTREACH_ROUTES.YOUTUBE_PLAYLISTING]: OutreachCategory.PLAYLISTING_CHANNELS,
    [OUTREACH_ROUTES.SPOTIFY]: OutreachCategory.SPOTIFY_PLAYLISTS,
    [OUTREACH_ROUTES.BLOGS]: OutreachCategory.MUSIC_BLOGS,
    [OUTREACH_ROUTES.CAMPAIGNS]: OutreachCategory.CAMPAIGNS,
    [OUTREACH_ROUTES.ANALYTICS]: "ANALYTICS" as OutreachCategory, // Special case for analytics
  };

  return categoryMap[routeName] ?? null;
}

/**
 * Map OutreachCategory to route name
 * @param category - OutreachCategory value
 * @returns Route name suitable for URL, or empty string if category has no route
 */
export function getRouteFromCategory(category: OutreachCategory): string {
  const routeMap: Record<OutreachCategory, string> = {
    [OutreachCategory.REACTION_CHANNELS]: OUTREACH_ROUTES.YOUTUBE_REACTION,
    [OutreachCategory.PLAYLISTING_CHANNELS]: OUTREACH_ROUTES.YOUTUBE_PLAYLISTING,
    [OutreachCategory.SPOTIFY_PLAYLISTS]: OUTREACH_ROUTES.SPOTIFY,
    [OutreachCategory.MUSIC_BLOGS]: OUTREACH_ROUTES.BLOGS,
    [OutreachCategory.CAMPAIGNS]: OUTREACH_ROUTES.CAMPAIGNS,
    [OutreachCategory.MUSIC_INDUSTRY]: "",
    [OutreachCategory.MESSAGE_CORRELATIONS]: "",
    ANALYTICS: OUTREACH_ROUTES.ANALYTICS,
  } as Record<OutreachCategory, string>;

  return routeMap[category] ?? "";
}

/**
 * Get display name for a route
 * @param routeName - Route name
 * @returns User-friendly display name
 */
export function getDisplayNameFromRoute(routeName: string): string {
  const displayMap: Record<string, string> = {
    [OUTREACH_ROUTES.YOUTUBE_REACTION]: "YouTube Reactions",
    [OUTREACH_ROUTES.YOUTUBE_PLAYLISTING]: "YouTube Playlisting",
    [OUTREACH_ROUTES.SPOTIFY]: "Spotify Playlists",
    [OUTREACH_ROUTES.BLOGS]: "Music Blogs",
    [OUTREACH_ROUTES.CAMPAIGNS]: "Campaigns",
    [OUTREACH_ROUTES.ANALYTICS]: "Analytics",
  };

  return displayMap[routeName] ?? routeName;
}

/**
 * Generate URL path for a given route
 * @param routeName - Route name from OUTREACH_ROUTES
 * @returns URL path starting with /outreach/
 */
export function getOutreachUrl(routeName: string): string {
  return `/outreach/${routeName}`;
}

/**
 * Parse outreach route from pathname
 * @param pathname - Current location pathname
 * @returns Route name or empty string if not an outreach route
 */
export function parseOutreachRoute(pathname: string): string {
  // Match /outreach/[route-name]
  const match = pathname.match(/^\/outreach\/([a-z-]+)(?:\/|$)/);
  return match ? match[1] : "";
}
